import React, { useRef, useEffect } from "react"
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from "chart.js"
import type { SeriesRow } from "~/api/snapshot_summaries"
import { formatCurrency } from "../utils"

Chart.register(CategoryScale, LinearScale, BarElement, Tooltip)

interface CallsDistributionsBarProps {
  series: SeriesRow[]
}

export default function CallsDistributionsBar({
  series,
}: CallsDistributionsBarProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<Chart<"bar"> | null>(null)

  const active = series
    .filter(
      (s) =>
        (s.quarter_activity?.called ?? 0) > 0 ||
        (s.quarter_activity?.dist ?? 0) > 0,
    )
    .sort(
      (a, b) =>
        (b.quarter_activity?.dist ?? 0) -
        (b.quarter_activity?.called ?? 0) -
        ((a.quarter_activity?.dist ?? 0) - (a.quarter_activity?.called ?? 0)),
    )

  const minChartWidth = Math.max(active.length * 22, 300)

  useEffect(() => {
    if (!canvasRef.current) return

    if (chartRef.current) {
      chartRef.current.destroy()
    }

    const ctx = canvasRef.current.getContext("2d")
    if (!ctx) return

    chartRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: active.map((s) => s.series),
        datasets: [
          {
            label: "Distributions",
            data: active.map((s) => s.quarter_activity?.dist ?? 0),
            backgroundColor: "#1d7a54",
            borderRadius: 2,
            maxBarThickness: 14,
            stack: "a",
          },
          {
            label: "Calls",
            data: active.map((s) => -(s.quarter_activity?.called ?? 0)),
            backgroundColor: "#b03a2e",
            borderRadius: 2,
            maxBarThickness: 14,
            stack: "a",
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                `${ctx.dataset.label}: ${formatCurrency(
                  Math.abs(ctx.raw as number),
                )}`,
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            ticks: {
              maxRotation: 90,
              font: { size: 9 },
              autoSkip: true,
            },
            grid: { display: false },
          },
          y: {
            stacked: true,
            ticks: {
              callback: (v) => formatCurrency(Math.abs(v as number)),
              font: { size: 10 },
            },
          },
        },
      },
    })

    const onResize = () => chartRef.current?.resize()
    window.addEventListener("resize", onResize)

    return () => {
      window.removeEventListener("resize", onResize)
      chartRef.current?.destroy()
      chartRef.current = null
    }
  }, [series])

  if (active.length === 0) {
    return <div className="b-ps-no-data">No capital activity this quarter.</div>
  }

  return (
    <div>
      <div
        className="b-ps-card__header"
        style={{ borderBottom: "none", padding: "0 0 10px 0" }}
      >
        <div className="b-ps-card__sub">
          Quarter activity · {active.length} series
        </div>
        <div style={{ fontSize: 12, color: "#6c7277" }}>
          <span style={{ color: "#1d7a54" }}>■</span> Distributions ▲ &nbsp;
          <span style={{ color: "#b03a2e" }}>■</span> Calls ▼
        </div>
      </div>
      <div className="b-ps-bar-scroll">
        <div style={{ width: "100%", minWidth: minChartWidth, height: 300 }}>
          <canvas ref={canvasRef} />
        </div>
      </div>
    </div>
  )
}
